// src/MainRouter.jsx
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';

/* ────────── cargas perezosas (lazy) ────────── */
// públicas
const Landing            = lazy(() => import('./components/Landing'));
const Login              = lazy(() => import('./components/Login'));
const Register           = lazy(() => import('./components/Register'));
const RecuperarPassword  = lazy(() => import('./components/RecuperarPassword'));

// dashboard genérico
const Dashboard          = lazy(() => import('./components/Dashboard'));

// administración / encargado
const ControlProduccion  = lazy(() => import('./components/ControlProduccion'));
const RutaTransportes    = lazy(() => import('./components/RutaTransportes'));
const Clientes           = lazy(() => import('./components/entidades/Clientes'));
const Empleados          = lazy(() => import('./components/entidades/Empleados'));
const Transportistas     = lazy(() => import('./components/entidades/Transportistas'));
const Usuarios           = lazy(() => import('./components/entidades/Usuarios'));
const Vehiculos          = lazy(() => import('./components/entidades/Vehiculos'));
const TiposVehiculos     = lazy(() => import('./components/entidades/TiposVehiculos'));
const Transportes        = lazy(() => import('./components/entidades/Transportes'));
const Repartos           = lazy(() => import('./components/entidades/Repartos'));
const LineasRepartos     = lazy(() => import('./components/entidades/LineasRepartos'));
const HorariosClientes   = lazy(() => import('./components/entidades/HorariosClientes'));
const Ropa               = lazy(() => import('./components/entidades/Ropa'));

const Pedidos            = lazy(() => import('./components/Pedidos'));
const NuevoPedido        = lazy(() => import('./components/NuevoPedido'));
const TotalesParaServir  = lazy(() => import('./components/TotalesParaServir'));
const TotalesMes         = lazy(() => import('./components/TotalesMes'));
const DiasSinReparto     = lazy(() => import('./components/DiasSinReparto'));
const AlgoritmoLLenadoCarros = lazy(() => import('./components/AlgoritmoLLenadoCarros'));
const GraficasPedidos    = lazy(() => import('./components/GraficasPedidos'));

// operario
const EmployeeDashboard  = lazy(() => import('./components/EmployeeDashboard'));

// cliente
const ClientePedidos         = lazy(() => import('./components/ClientePedidos'));
const ClienteHistoricoPedidos= lazy(() => import('./components/ClienteHistoricoPedidos'));

// otros
const RopaCliente        = lazy(() => import('./components/RopaCliente'));
const Almacen            = lazy(() => import('./components/Almacen'));
const ComprasReposición  = lazy(() => import('./components/ComprasReposición'));
const EntradasSalidas    = lazy(() => import('./components/EntradasSalidas'));

/* ────────── spinner de carga ────────── */
const Loading = () => (
  <div style={{ display:'flex', justifyContent:'center', padding:'40px 0' }}>
    <CircularProgress />
  </div>
);

/* ==================================================================== */
/*  Sub-routers por rol                                                 */
/* ==================================================================== */
function AdminRoutes({ fechaHoy }) {
  return (
    <Routes>
      {/* panel principal */}
      <Route path="/control-produccion"          element={<ControlProduccion fechaHoy={fechaHoy}/>}/>
      <Route path="/dashboard"                   element={<Dashboard/>}/>

      {/* entidades */}
      <Route path="/clientes"                    element={<Clientes/>}/>
      <Route path="/empleados"                   element={<Empleados/>}/>
      <Route path="/transportistas"              element={<Transportistas/>}/>
      <Route path="/usuarios"                    element={<Usuarios/>}/>
      <Route path="/vehiculos"                   element={<Vehiculos/>}/>
      <Route path="/tipos-vehiculos"             element={<TiposVehiculos/>}/>
      <Route path="/transportes"                 element={<Transportes/>}/>
      <Route path="/repartos"                    element={<Repartos/>}/>
      <Route path="/lineas-repartos"             element={<LineasRepartos/>}/>
      <Route path="/horarios-clientes"           element={<HorariosClientes/>}/>
      <Route path="/ropa"                        element={<Ropa/>}/>

      {/* logística / pedidos */}
      <Route path="/pedidos"                     element={<Pedidos/>}/>
      <Route path="/nuevo-pedido"                element={<NuevoPedido/>}/>
      <Route path="/totales-para-servir"         element={<TotalesParaServir/>}/>
      <Route path="/totales-mes"                 element={<TotalesMes/>}/>
      <Route path="/dias-sin-reparto"            element={<DiasSinReparto/>}/>
      <Route path="/algoritmo-llenado-carros"    element={<AlgoritmoLLenadoCarros/>}/>
      <Route path="/graficas-pedidos"            element={<GraficasPedidos/>}/>

      {/* almacén / compras */}
      <Route path="/almacen"                     element={<Almacen/>}/>
      <Route path="/compras_reposición"          element={<ComprasReposición/>}/>
      <Route path="/entradas_salidas"            element={<EntradasSalidas/>}/>
      <Route path="/ropa_cliente"                element={<RopaCliente/>}/>

      {/* redirección por defecto */}
      <Route path="*" element={<Navigate to="/control-produccion" replace/>}/>
    </Routes>
  );
}

function EncargadoRoutes(props){           // ⟹ de momento mismo contenido que Admin
  return <AdminRoutes {...props} />;
}

function EmpleadoRoutes({ produccionData, fechaHoy }){
  return (
    <Routes>
      <Route path="/employee-dashboard" element={<EmployeeDashboard data={produccionData} fecha={fechaHoy}/>}/>
      <Route path="*" element={<Navigate to="/employee-dashboard" replace/>}/>
    </Routes>
  );
}

function ClienteRoutes(){
  return (
    <Routes>
      <Route path="/cliente-pedidos"            element={<ClientePedidos/>}/>
      <Route path="/cliente-historico-pedidos"  element={<ClienteHistoricoPedidos/>}/>
      <Route path="*" element={<Navigate to="/cliente-pedidos" replace/>}/>
    </Routes>
  );
}

/* ==================================================================== */
/*  MAIN ROUTER                                                         */
/* ==================================================================== */
export default function MainRouter({ token, rol, handleLogin, produccionData, fechaHoy }) {

  /* ----------- 1 · Sin sesión → sólo páginas públicas --------------- */
  if (!token) {
    return (
      <Suspense fallback={<Loading/>}>
        <Routes>
          <Route path="/landing"        element={<Landing/>}/>
          <Route path="/register"       element={<Register/>}/>
          <Route path="/recovery-pass"  element={<RecuperarPassword/>}/>
          <Route path="/login"          element={<Login handleLogin={handleLogin}/>}/>
          <Route path="*"               element={<Navigate to="/login" replace/>}/>
        </Routes>
      </Suspense>
    );
  }

  /* ----------- 2 · Con sesión → sub-router según rol ---------------- */
  const routerByRole = {
    Administrador : <AdminRoutes   fechaHoy={fechaHoy} />,
    Encargado     : <EncargadoRoutes fechaHoy={fechaHoy} />,
    Empleado      : <EmpleadoRoutes produccionData={produccionData} fechaHoy={fechaHoy}/>,
    Cliente       : <ClienteRoutes />,
  };

  const innerRouter = routerByRole[rol] ?? <Navigate to="/landing" replace/>;

  return (
    <Suspense fallback={<Loading/>}>
      {innerRouter}
    </Suspense>
  );
}
